import PropTypes from 'prop-types'
import { useTheme } from '@emotion/react'
import { graphql } from 'gatsby'
import { rgba, ellipsis } from 'polished'
import { Badge, Inline, ListItem } from 'chaoskit/src/components'
import { StylesContainerVariables } from 'chaoskit/src/components/Container'
import { link } from 'chaoskit/src/assets/styles/utility'

import BaseLayout from '~layouts/BaseLayout'
import Data from '~components/Data'
import Icon from '~components/Icon'
import { HeroTitle, HeroContent, HeroTitleSub } from '~partials/Hero'
import CustomerQuote from '~partials/home/CustomerQuote'
import hero from '~media/home/hero.png'
import Image from '~components/Image'
import ButtonLink from '~components/ButtonLink'
import TextLink from '~components/TextLink'
import HelmetSEO from '~partials/global/HelmetSEO'
import MutinyTag from '~components/MutinyTag'
import {
  HomeHero,
  HomeHeroGrid,
  HomeHeroGridContent,
  HomeHeroGridImage,
} from '~partials/home/Hero'
import {
  FirstSection,
  SecondSection,
  ThirdSection,
  FourthSection,
  FifthSection,
  SixthSection,
} from '~partials/home/Sections'

const HomeHeroAnnouncement = ({
  announcement: { style, badge, text, link: announcementLink },
}) => {
  const theme = useTheme()

  return (
    <TextLink
      href={announcementLink.url}
      css={[
        link.contrast(theme),
        {
          boxShadow: theme.boxShadow.base,
          paddingTop: theme.space.xsmall,
          paddingBottom: theme.space.xsmall,
          paddingLeft: theme.space.xsmall,
          paddingRight: theme.space.small,
          ...theme.text.xsmall,
          display: 'inline-grid',
          gridTemplateColumns: 'auto 1fr',
          gap: theme.space.small,
          alignItems: 'center',
          borderRadius: (theme.height.small + theme.space.xsmall) / 2,
          transition: `transform ${theme.timing.base} ${theme.transition.bounce}`,
          transformOrigin: 'center center',

          '&:hover, &:focus': {
            transform: 'scale(1.025)',
          },

          [theme.mq.small__max]: {
            position: 'relative',
            left: '50%',
            transform: 'translateX(-50%)',

            '&:hover, &:focus': {
              transform: 'scale(1.025) translateX(-50%)',
            },
          },
        },

        style === 'orange' && {
          background: theme.color.dark.overlay,
        },

        style === 'green' && {
          background: rgba(theme.contrast.base, 0.2),
        },
      ]}
    >
      {badge && (
        <Badge
          type={style === 'orange' ? 'warning' : 'primary'}
          rounded
          css={{
            cursor: 'pointer', // Resets default cursor style to avoid UX confusion
          }}
        >
          {badge}
        </Badge>
      )}
      <div css={[ellipsis()]}>
        <Data>{text}</Data>
        <Icon
          icon="arrow-right"
          css={{
            top: 'auto', // Resets some default offset we normally have for inline text since the grid is taking care of that
            marginLeft: theme.space.small,
          }}
        />
      </div>
    </TextLink>
  )
}

HomeHeroAnnouncement.propTypes = {
  announcement: PropTypes.object.isRequired,
}

const Home = ({
  data: {
    file: {
      childMdx: { frontmatter: pageData },
    },
  },
}) => {
  const theme = useTheme()

  return (
    <BaseLayout
      headerProps={{
        showPrimaryLogo: true,
        contrast: false,
        hideAnnouncementBar: pageData.hero.announcement.show,
      }}
      footerProps={{ hideCTA: true }}
    >
      <HelmetSEO title={pageData.title} description={pageData.description} />
      <MutinyTag />
      <HomeHero
        contrast={false}
        css={{
          paddingTop: '70px',

          [theme.mq.medium]: {
            paddingTop: 0,
            paddingBottom: 0,
            minHeight: '650px',
          },
        }}
      >
        <HeroContent>
          <HomeHeroGrid
            css={{
              maxWidth: StylesContainerVariables.large,

              [theme.mq.medium]: {
                paddingTop: `${theme.space['2xlarge']}px !important`,
              },
            }}
          >
            <HomeHeroGridImage>
              <Image src={hero} alt="hero" />
            </HomeHeroGridImage>
            <HomeHeroGridContent
              css={{
                [theme.mq.medium]: {
                  width: '90%',
                },
              }}
            >
              {pageData.hero.announcement.show && (
                <HomeHeroAnnouncement
                  announcement={pageData.hero.announcement}
                />
              )}
              <HeroTitle as="h2" size="base" css={{ marginBottom: '20px' }}>
                <Data>{pageData.hero.heading}</Data>
              </HeroTitle>
              <HeroTitleSub
                css={{
                  fontSize: '24px',
                  lineHeight: '1.4',
                  marginBottom: '37px',
                }}
              >
                <Data>{pageData.hero.blurb}</Data>
              </HeroTitleSub>
              <div css={{ marginTop: theme.space.large }}>
                {pageData.hero.extraText && (
                  <div
                    css={{
                      ...theme.text.xlarge,
                      color: theme.brand.violet,
                      marginBottom: '30px',
                      fontWeight: theme.fontWeight.bold,

                      [theme.mq.small__max]: {
                        display: 'none',
                      },
                    }}
                  >
                    <Data>{pageData.hero.extraText}</Data>
                  </div>
                )}
                <Inline
                  size="medium"
                  css={{
                    justifyContent: 'flex-start',
                    alignItems: 'flex-start',
                    [theme.mq.small__max]: {
                      display: 'block',
                    },
                  }}
                >
                  {pageData.hero.buttons.map((button) => (
                    <ListItem
                      key={button.buttonURL}
                      css={{
                        '.CK__Button.CK__Button--default': {
                          border: '1px solid #B3B3B3',
                        },
                      }}
                    >
                      <ButtonLink
                        noContrast
                        type={button.buttonType}
                        url={button.buttonURL}
                        css={{
                          [theme.mq.small__max]: {
                            width: '100%',
                          },
                        }}
                      >
                        {button.buttonText}
                      </ButtonLink>
                    </ListItem>
                  ))}
                </Inline>
                {pageData.hero.extraText && (
                  <div
                    css={{
                      color: theme.brand.violet,
                      marginTop: theme.space.base,
                      fontWeight: theme.fontWeight.bold,
                      textAlign: 'center',

                      [theme.mq.medium]: {
                        display: 'none',
                      },
                    }}
                  >
                    <Data>{pageData.hero.extraText}</Data>
                  </div>
                )}
              </div>
            </HomeHeroGridContent>
          </HomeHeroGrid>
        </HeroContent>
      </HomeHero>
      <FirstSection data={pageData.firstSection} />
      <SecondSection data={pageData.secondSection} />
      <ThirdSection data={pageData.thirdSection} />
      <CustomerQuote hideCta={false} />
      <FourthSection data={pageData.fourthSection} />
      <FifthSection data={pageData.fifthSection} />
      <SixthSection data={pageData.sixthSection} />
    </BaseLayout>
  )
}

Home.propTypes = {
  data: PropTypes.object.isRequired,
}

export const pageQuery = graphql`
  query HomePageData {
    file(name: { eq: "home" }, absolutePath: { regex: "/src/data/" }) {
      childMdx {
        frontmatter {
          title
          description
          hero {
            announcement {
              show
              style
              badge
              text
              link {
                url
              }
            }
            heading
            blurb
            extraText
            buttons {
              buttonText
              buttonType
              buttonURL
            }
          }
          firstSection {
            heading
            subHeading
            buttonText
            buttonURL
          }
          secondSection {
            heading
            blurb
            items {
              icon
              blurb
            }
            buttonText
            buttonURL
          }
          thirdSection {
            items {
              heading
              subHeading
              blurb
              icon
            }
          }
          fourthSection {
            heading
            items {
              icon
              blurb
            }
          }
          fifthSection {
            heading
            blurb
            videoURL
            videoImage
          }
          sixthSection {
            heading
            blurb
            sectionType
            buttonText
            buttonURL
            sectionImage
          }
        }
      }
    }
  }
`

export default Home
